import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
const TITLE_ID = 'confirm-changes-modal-title';
const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';
export function ConfirmChangesModal({ sections, isApplying, onGoBack, onApply }) {
    const overlayRef = useRef(null);
    const dialogRef = useRef(null);
    const goBackButtonRef = useRef(null);
    useEffect(() => {
        var _a;
        const previouslyFocused = document.activeElement;
        (_a = goBackButtonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        const overlay = overlayRef.current;
        const siblings = (overlay === null || overlay === void 0 ? void 0 : overlay.parentElement)
            ? Array.from(overlay.parentElement.children).filter((node) => node !== overlay)
            : [];
        const restoreAttributes = siblings.map((node) => ({
            inert: node.getAttribute('inert'),
            ariaHidden: node.getAttribute('aria-hidden'),
        }));
        siblings.forEach((node) => {
            node.setAttribute('inert', '');
            node.setAttribute('aria-hidden', 'true');
        });
        return () => {
            siblings.forEach((node, index) => {
                const restore = restoreAttributes[index];
                if (restore.inert === null) {
                    node.removeAttribute('inert');
                }
                else {
                    node.setAttribute('inert', restore.inert);
                }
                if (restore.ariaHidden === null) {
                    node.removeAttribute('aria-hidden');
                }
                else {
                    node.setAttribute('aria-hidden', restore.ariaHidden);
                }
            });
            previouslyFocused === null || previouslyFocused === void 0 ? void 0 : previouslyFocused.focus();
        };
    }, []);
    const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
            event.stopPropagation();
            onGoBack();
            return;
        }
        if (event.key !== 'Tab' || !dialogRef.current) {
            return;
        }
        const focusable = Array.from(dialogRef.current.querySelectorAll(FOCUSABLE_SELECTOR));
        if (focusable.length === 0) {
            return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        }
        else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    };
    return (_jsx("div", { className: "confirm-changes-modal-overlay", "data-test": "confirm_changes_modal_overlay", onClick: onGoBack, ref: overlayRef, children: _jsxs("div", { className: "confirm-changes-modal", "data-test": "confirm_changes_modal", role: "dialog", "aria-modal": "true", "aria-labelledby": TITLE_ID, ref: dialogRef, onClick: (event) => { event.stopPropagation(); }, onKeyDown: handleKeyDown, children: [_jsxs("div", { className: "confirm-changes-modal__header", children: [_jsx("h4", { className: "confirm-changes-modal__title", id: TITLE_ID, children: "Confirm Changes" }), _jsx("p", { className: "confirm-changes-modal__subtitle", children: "Review what will change before applying. These updates will take effect immediately." })] }), _jsx("div", { className: "confirm-changes-modal__body", children: sections.map((section) => (_jsxs("div", { className: "confirm-changes-modal__section", "data-test": "confirm_changes_section", "data-section-name": section.name, children: [_jsxs("h5", { className: "confirm-changes-modal__section-title all-caps", children: [_jsx("span", { className: "confirm-changes-modal__bullet", "aria-hidden": "true" }), section.name] }), section.lines.map((line, index) => (_jsxs("div", { className: "confirm-changes-modal__line", "data-test": "confirm_changes_line", children: [line.label && _jsxs("span", { className: "confirm-changes-modal__label", children: [line.label, ": "] }), _jsx("span", { className: "text-danger", children: line.oldValue }), ' \u2192 ', _jsx("span", { className: "text-success", children: line.newValue })] }, index)))] }, section.name))) }), _jsxs("div", { className: "confirm-changes-modal__footer", children: [_jsx("button", { type: "button", className: "btn btn-default", "data-test": "confirm_changes_go_back_button", ref: goBackButtonRef, onClick: onGoBack, disabled: isApplying, children: "Go Back" }), ' ', _jsx("button", { type: "button", className: "btn btn-primary", "data-test": "confirm_changes_apply_button", onClick: onApply, disabled: isApplying, children: isApplying ? 'Applying...' : 'Apply Changes' })] })] }) }));
}
